import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Table,
  Typography,
  Modal,
  Tooltip,
  Space,
  Descriptions,
  Tag,
  DatePicker,
} from "antd";
import { Severty, ShowToast } from "../helper/toast";
import apiPath from "../constants/apiPath";
import useRequest from "../hooks/useRequest";
import { EyeOutlined, SearchOutlined, UserAddOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const statusOptions = ["all", "Pending", "Ongoing", "In Progress", "Confirmed", "Completed", "Cancelled"];

const AdminOrdersPage = () => {
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Updated: Map API response to consistent format
  const mapOrderData = (order) => {
    const vendorResponse = order.vendor_responses?.length
      ? order.vendor_responses.reduce((prev, curr) => {
          const prevResponse = prev.response?.toLowerCase();
          const currResponse = curr.response?.toLowerCase();
          if (currResponse === "accepted") return curr;
          if (prevResponse === "accepted") return prev;
          if (currResponse === "completed" && prevResponse !== "accepted") return curr;
          if (prevResponse === "completed" && currResponse !== "accepted") return prev;
          if (currResponse === "rejected" && !["accepted", "completed"].includes(prevResponse)) return curr;
          if (prevResponse === "rejected" && !["accepted", "completed"].includes(currResponse)) return prev;
          if (currResponse === "pending" && !["accepted", "completed", "rejected"].includes(prevResponse)) return curr;
          return prev;
        }, order.vendor_responses[0])
      : null;

    // === SPLIT time_range string into start/end ===
    let delivery_time_start = null;
    let delivery_time_end = null;
    let delivery_time_range = "Not Set";

    if (order.time_range && order.time_range.includes("-")) {
      const [start, end] = order.time_range.split("-").map(s => s.trim());
      if (start && end) {
        delivery_time_start = `1970-01-01T${start}:00`; // dummy date for formatting
        delivery_time_end = `1970-01-01T${end}:00`;
        delivery_time_range = `${start} - ${end}`;
      }
    }

    // === STATION + LOCATION ===
    const station = order.user?.station || "";
    const locationWithStation = station
      ? `${order.location} (${station})`
      : order.location || "Not Provided";

    return {
      ...order,
      id: order.id,
      order_id: order.order_id,
      created_at: order.created_at,
      user_name: order.user?.name || "N/A",
      country: order.user?.country || "N/A",
      station: order.user?.station || "N/A",
      service_name: order.service?.name || "N/A",
      product_name: order.product?.name || "N/A",
      booking_status: order.booking_status,
      assigned_vendor: order.assigned_vendor?.name || "Not assigned",
      vendor_id: order.assigned_vendor?.id,
      quantity: order.quantity,
      special_requirements: order.special_requirements,
      // === LOCATION WITH STATION ===
      location: locationWithStation,
      station,
      documents: order.documents,
      document_type: order.document_type,
      total_amount: order.total_amount,
      vendor_status: vendorResponse ? vendorResponse.response : null,
      rejection_reason: vendorResponse ? vendorResponse.rejection_reason : null,
      vendor_responses: order.vendor_responses || [],
      // === DELIVERY FIELDS ===
      delivery_date: order.delivery_date,
      delivery_time_start,
      delivery_time_end,
      delivery_time_range,
      // === RECIPIENT FIELDS (renamed from delivery_person) ===
      recipient_name: order.delivery_person_name || "",
      recipient_phone: order.delivery_person_phone || "",
      // === BACKWARD COMPATIBILITY ===
      delivery_person_name: order.delivery_person_name || "",
      delivery_person_phone: order.delivery_person_phone || "",
    };
  };

  // Fetch orders with pagination and filters
  const fetchOrders = (page = 1, pageSize = 10, filters = {}) => {
    setLoading(true);

    let queryParams = `page=${page}&page_size=${pageSize}`;

    if (filters.searchKeyword && filters.searchKeyword.trim()) {
      queryParams += `&search=${encodeURIComponent(filters.searchKeyword.trim())}`;
    }

    if (filters.status && filters.status !== "all") {
      queryParams += `&status=${encodeURIComponent(filters.status)}`;
    }

    if (filters.dateRange && filters.dateRange.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      queryParams += `&start_date=${startDate.format('YYYY-MM-DD')}&end_date=${endDate.format('YYYY-MM-DD')}`;
    }

    request({
      url: `${apiPath.orderList || "/order/order_list"}?${queryParams}`,
      method: "GET",
      onSuccess: ({ data, pagination: paginationData }) => {
        const mappedOrders = data.map(mapOrderData);
        setOrders(mappedOrders || []);
        setPagination({
          current: paginationData?.current_page || 1,
          pageSize: paginationData?.page_size || 10,
          total: paginationData?.total_count || 0,
        });
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        ShowToast("Failed to fetch orders", Severty.ERROR);
      },
    });
  };

  // Fetch active vendors
  const fetchVendors = (bookingOrderId) => {
    let url = `${apiPath.activeVendorList || "/active_vendor"}`;
    if (bookingOrderId) {
      url += `?booking_id=${bookingOrderId}`;
    }

    request({
      url,
      method: "GET",
      onSuccess: (response) => {
        if (response.status) {
          setVendors(response.data || []);
        } else {
          ShowToast(response.message || "No active vendors found", Severty.ERROR);
          setVendors([]);
        }
      },
      onError: () => {
        // ShowToast("Failed to fetch vendors", Severty.ERROR);
        setVendors([]);
      },
    });
  };

  // Table pagination
  const handleTableChange = (pagination) => {
    fetchOrders(pagination.current, pagination.pageSize, {
      searchKeyword,
      status: statusFilter,
      dateRange,
    });
  };

  useEffect(() => {
    fetchOrders(pagination.current, pagination.pageSize);
  }, []);

  // Debounced search
  const handleSearch = (keyword) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setSearchKeyword(keyword);
      fetchOrders(1, pagination.pageSize, {
        searchKeyword: keyword,
        status: statusFilter,
        dateRange,
      });
    }, 500);
    setSearchTimeout(timeout);
  };

  const handleSearchChange = (e) => handleSearch(e.target.value);
  const handleSearchSubmit = (values) => {
    const { keyword, date_range } = values;
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchKeyword(keyword);
    setDateRange(date_range);
    fetchOrders(1, pagination.pageSize, {
      searchKeyword: keyword,
      status: statusFilter,
      dateRange: date_range,
    });
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    fetchOrders(1, pagination.pageSize, {
      searchKeyword,
      status: value,
      dateRange,
    });
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    fetchOrders(1, pagination.pageSize, {
      searchKeyword,
      status: statusFilter,
      dateRange: dates,
    });
  };

  const resetFilters = () => {
    setSearchKeyword("");
    setStatusFilter("all");
    setDateRange(null);
    searchForm.resetFields();
    if (searchTimeout) clearTimeout(searchTimeout);
    fetchOrders(1, pagination.pageSize);
  };

  useEffect(() => {
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchTimeout]);

  // View Order
  const openViewModal = (order) => {
    setViewLoading(true);
    setViewingOrder(null);
    setViewModalVisible(true);

    request({
      url: `${apiPath.orderDetail || "/order/get_order"}?id=${order.id}`,
      method: "GET",
      onSuccess: (response) => {
        const orderData = response?.data || order;
        setViewingOrder(mapOrderData(orderData));
        setViewLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.error || "Failed to fetch order details", Severty.ERROR);
        setViewLoading(false);
        setViewingOrder(mapOrderData(order));
      },
    });
  };

  const closeViewModal = () => {
    setViewModalVisible(false);
    setViewingOrder(null);
    setViewLoading(false);
  };

  // Assign Vendor
  const openAssignModal = (order) => {
    setSelectedOrder(order);
    form.resetFields();
    fetchVendors(order.id);
    setAssignModalVisible(true);
  };

  const onAssignSubmit = (values) => {
    setLoading(true);
    request({
      url: `${apiPath.assignVendor || "/assign_vendor"}`,
      method: "POST",
      data: {
        booking_id: selectedOrder.id,
        vendor_id: values.vendor_id,
      },
      onSuccess: (response) => {
        if (response.status) {
          ShowToast(response.message || "Vendor assigned successfully", Severty.SUCCESS);
          setAssignModalVisible(false);
          fetchOrders(pagination.current, pagination.pageSize, {
            searchKeyword,
            status: statusFilter,
            dateRange,
          });
        } else {
          ShowToast(response.error || "Failed to assign vendor", Severty.ERROR);
        }
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.error || "Failed to assign vendor", Severty.ERROR);
        setLoading(false);
      },
    });
  };

  // Status Tag
  const getStatusTag = (status) => {
    let color;
    switch (status?.toLowerCase()) {
      case "pending": color = "orange"; break;
      case "confirmed": color = "blue"; break;
      case "completed": color = "green"; break;
      case "cancelled": color = "red"; break;
      case "ongoing": color = "purple"; break;
      case "in progress": color = "cyan"; break;
      default: color = "gray";
    }
    return <Tag color={color}>{status?.toUpperCase()}</Tag>;
  };

  // Vendor Response
  const getVendorResponse = (order) => {
    if (!order.vendor_responses?.length) {
      return order.assigned_vendor ? <Tag color="orange">Pending Response</Tag> : "Not Assigned";
    }
    const vendorResponse = order.vendor_responses.reduce((prev, curr) => {
      const prevResponse = prev.response?.toLowerCase();
      const currResponse = curr.response?.toLowerCase();
      if (currResponse === "accepted") return curr;
      if (prevResponse === "accepted") return prev;
      if (currResponse === "completed" && prevResponse !== "accepted") return curr;
      if (prevResponse === "completed" && currResponse !== "accepted") return prev;
      if (currResponse === "rejected" && !["accepted", "completed"].includes(prevResponse)) return curr;
      if (prevResponse === "rejected" && !["accepted", "completed"].includes(currResponse)) return prev;
      if (currResponse === "pending" && !["accepted", "completed", "rejected"].includes(prevResponse)) return curr;
      return prev;
    }, order.vendor_responses[0]);

    const response = vendorResponse.response?.toLowerCase();
    switch (response) {
      case "accepted": return <Tag color="green">Accepted</Tag>;
      case "pending": return <Tag color="orange">Pending</Tag>;
      case "completed": return <Tag color="green">Completed</Tag>;
      default: return <Tag color="red">Rejected</Tag>;
    }
  };

  const canAssignVendor = (order) => {
    if (order.booking_status?.toLowerCase() !== "pending") return false;
    if (!order.vendor_responses?.length) return true;
    return !order.vendor_responses.some(res => res.response?.toLowerCase() === "accepted");
  };

  // Table Columns
  const orderColumns = [
    {
      title: "Booking ID",
      dataIndex: "order_id",
      key: "order_id",
      render: (text) => <strong>{text}</strong>,
      width: 130,
      fixed: 'left',
    },
    {
      title: "Booking Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => new Date(text).toLocaleDateString(),
      width: 120,
    },
    {
      title: "Client Name",
      dataIndex: "user_name",
      key: "user_name",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Station",
      dataIndex: "station",
      key: "station",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Service Name",
      dataIndex: "service_name",
      key: "service_name",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Delivery Date",
      dataIndex: "delivery_date",
      key: "delivery_date",
      render: (date) => date ? new Date(date).toLocaleDateString() : "Not Set",
      width: 130,
    },
    {
      title: "Delivery Time",
      dataIndex: "delivery_time_range",
      key: "delivery_time_range",
      width: 130,
    },
    {
      title: "Status",
      dataIndex: "booking_status",
      key: "booking_status",
      render: (status) => getStatusTag(status),
      width: 120,
    },
    {
      title: "Vendor Name",
      dataIndex: "assigned_vendor",
      key: "assigned_vendor",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) => amount ? `$${parseFloat(amount).toLocaleString()}` : "N/A",
      width: 120,
    },
    {
      title: "Vendor Response",
      key: "vendor_response",
      render: (_, record) => getVendorResponse(record),
      width: 130,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Order">
            <Button size="small" icon={<EyeOutlined />} onClick={() => openViewModal(record)} />
          </Tooltip>
          <Tooltip title="Assign Vendor">
            <Button
              size="small"
              icon={<UserAddOutlined />}
              onClick={() => openAssignModal(record)}
              disabled={!canAssignVendor(record)}
            />
          </Tooltip>
        </Space>
      ),
      width: 100,
      fixed: 'right',
    },
  ];

  return (
    <div className="layout-content">
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>Manage Orders</Title>
          </Col>
        </Row>

        {/* Search and Filter */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form form={searchForm} layout="vertical" onFinish={handleSearchSubmit}>
            <Row gutter={[16, 16]} align="bottom">
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Search Orders" name="keyword">
                  <Input
                    placeholder="Search by booking ID or client name..."
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={handleSearchChange}
                    onPressEnter={(e) => {
                      e.preventDefault();
                      handleSearchSubmit(searchForm.getFieldsValue());
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Status" name="status">
                  <Select onChange={handleStatusFilterChange}>
                    {statusOptions.map((status) => (
                      <Option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Date Range" name="date_range">
                  <RangePicker style={{ width: '100%' }} onChange={handleDateRangeChange} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label=" " colon={false}>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                      Search
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {(searchKeyword || statusFilter !== "all" || dateRange) && (
            <div style={{ marginTop: 16, padding: 8, background: "#f0f8ff", borderRadius: 6 }}>
              <Space wrap>
                <strong>Active Filters:</strong>
                {searchKeyword && <Tag color="blue">Search: "{searchKeyword}"</Tag>}
                {statusFilter !== "all" && <Tag color="blue">Status: {statusFilter}</Tag>}
                {dateRange && (
                  <Tag color="blue">
                    Date: {dateRange[0].format('YYYY-MM-DD')} to {dateRange[1].format('YYYY-MM-DD')}
                  </Tag>
                )}
                <Button type="link" size="small" onClick={resetFilters}>
                  Clear All
                </Button>
              </Space>
            </div>
          )}
        </Card>

        <Table
          rowKey="id"
          columns={orderColumns}
          dataSource={orders}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          className="mt-3"
          scroll={{ x: 1800, y: 'calc(100vh - 400px)' }}
          size="middle"
          bordered
          sticky
        />
      </Card>

      {/* Assign Vendor Modal */}
      <Modal
        open={assignModalVisible}
        footer={null}
        onCancel={() => setAssignModalVisible(false)}
      >
        <div className="modal_header">Assign Vendor to Order</div>
        {vendors.length === 0 ? (
          <div style={{ padding: "20px", textAlign: "center" }}>
            No active vendors available.
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={onAssignSubmit}>
            <Form.Item label="Select Vendor" name="vendor_id" rules={[{ required: true }]}>
              <Select placeholder="Choose a vendor" loading={loading}>
                {vendors.map((vendor) => (
                  <Option key={vendor.id} value={vendor.id}>
                    {vendor.name} ({vendor.email})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Assign Vendor
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* View Order Modal */}
      <Modal
        open={viewModalVisible}
        onCancel={closeViewModal}
        footer={[<Button key="close" onClick={closeViewModal}>Close</Button>]}
        width={900}
      >
        <div className="modal_header">Order Details</div>
        {viewingOrder && (
          <div style={{ padding: "5px 0" }}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Booking ID">
                <strong>{viewingOrder.order_id}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Booking Date">
                {new Date(viewingOrder.created_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Client Name">{viewingOrder.user_name}</Descriptions.Item>
                            <Descriptions.Item label="Country">
                              {viewingOrder.country}
                            </Descriptions.Item>
                            <Descriptions.Item label="Station">
                              {viewingOrder.station}
                            </Descriptions.Item>
              <Descriptions.Item label="Service Name">{viewingOrder.service_name}</Descriptions.Item>
              <Descriptions.Item label="Product Name">{viewingOrder.product_name}</Descriptions.Item>
              <Descriptions.Item label="Quantity">{viewingOrder.quantity || "Not provided"}</Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                {viewingOrder.total_amount ? `$${parseFloat(viewingOrder.total_amount).toLocaleString()}` : "N/A"}
              </Descriptions.Item>

              {/* Delivery Fields */}
              <Descriptions.Item label="Delivery Date">
                {viewingOrder.delivery_date ? new Date(viewingOrder.delivery_date).toLocaleDateString() : "Not Set"}
              </Descriptions.Item>
              <Descriptions.Item label="Delivery Time">
                {viewingOrder.delivery_time_range}
              </Descriptions.Item>
              {/* Updated: Recipient (not Delivery Person) */}
              <Descriptions.Item label="Receiver Person Name">
                {viewingOrder.recipient_name || "Not Provided"}
              </Descriptions.Item>
              <Descriptions.Item label="Receiver Person Phone">
                {viewingOrder.recipient_phone || "Not Provided"}
              </Descriptions.Item>

              <Descriptions.Item label="Location">
                <span>{viewingOrder.location}</span>
                {viewingOrder.station && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {viewingOrder.station}
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Special Requirements">
                {viewingOrder.special_requirements || "None"}
              </Descriptions.Item>
              <Descriptions.Item label="Document Type">{viewingOrder.document_type || "Not provided"}</Descriptions.Item>
              <Descriptions.Item label="Documents">
                {viewingOrder.documents ? (
                  <a href={viewingOrder.documents} target="_blank" rel="noopener noreferrer">
                    View Documents
                  </a>
                ) : (
                  "None"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Status">{getStatusTag(viewingOrder.booking_status)}</Descriptions.Item>
              <Descriptions.Item label="Assigned Vendor">{viewingOrder.assigned_vendor}</Descriptions.Item>
              <Descriptions.Item label="Vendor Responses">
                {viewingOrder.vendor_responses.length ? (
                  <Table
                    rowKey="id"
                    columns={[
                      { title: "Vendor", dataIndex: "vendor_name", key: "vendor_name", width: 150 },
                      {
                        title: "Response",
                        dataIndex: "response",
                        key: "response",
                        render: (response) => {
                          const status = response?.toLowerCase();
                          switch (status) {
                            case "accepted": return <Tag color="green">Accepted</Tag>;
                            case "pending": return <Tag color="orange">Pending</Tag>;
                            case "completed": return <Tag color="green">Completed</Tag>;
                            default: return <Tag color="red">Rejected</Tag>;
                          }
                        },
                        width: 100,
                      },
                      { title: "Rejection Reason", dataIndex: "rejection_reason", key: "rejection_reason", render: (r) => r || "None", width: 150 },
                      { title: "POD Status", dataIndex: "pod_status", key: "pod_status", width: 100 },
                      {
                        title: "POD Document",
                        dataIndex: "pod_document",
                        key: "pod_document",
                        render: (doc) => doc ? <a href={doc} target="_blank" rel="noopener noreferrer">View</a> : "None",
                        width: 120,
                      },
                    ]}
                    dataSource={viewingOrder.vendor_responses}
                    pagination={false}
                    size="small"
                    scroll={{ x: 600 }}
                  />
                ) : (
                  "No vendor responses"
                )}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
        {!viewingOrder && viewLoading && (
          <div style={{ textAlign: "center", padding: "20px" }}>Loading order details...</div>
        )}
      </Modal>
    </div>
  );
};

export default AdminOrdersPage;